import {
  EXPERTISE_LABELS,
  type ExpertiseCategoryKey,
} from "@/lib/config/services.config";
import { shapeAsset } from "@/lib/shapes";

export const SERVICES = [
  {
    id: "strategy",
    title: EXPERTISE_LABELS.strategy,
    description:
      "We have solved a wide range of technical problems in production—from reliable mobile data on poor networks to analytics-rich sites and integrated systems. We bring that experience to your constraints, risks, and roadmap.",
    color: "pink",
    icon: shapeAsset("cube", "coral"),
  },
  {
    id: "design",
    title: EXPERTISE_LABELS.design,
    description:
      "You cannot design something you do not understand. After we have learned your product and context, our process turns abstract goals into clear, buildable experience and interface decisions.",
    color: "teal",
    icon: shapeAsset("pyramid", "pink"),
  },
  {
    id: "development",
    title: EXPERTISE_LABELS.development,
    description:
      "Engineering and design stay paired as we ship. Our team works in Elixir, Phoenix, React Native, Next.js, JavaScript, and the surrounding ecosystem—with attention to maintainability and real-world operations.",
    color: "yellow",
    icon: shapeAsset("rectangle-upright", "blue"),
  },
  {
    id: "prototyping",
    title: EXPERTISE_LABELS.prototyping,
    description:
      "When the goal is learning fast, we scope prototypes and MVPs that prove the riskiest assumptions without overbuilding—so you can decide what deserves a full product investment.",
    color: "blue",
    icon: shapeAsset("arch", "teal"),
    href: "/prototype",
  },
  {
    id: "brand",
    title: EXPERTISE_LABELS.brand,
    description:
      "Strong products need a brand that carries across touchpoints. We can build identity from the ground up or evolve what you have so product, site, and story stay coherent as you grow.",
    color: "peach",
    icon: shapeAsset("ball", "yellow"),
  },
  {
    id: "marketing",
    title: EXPERTISE_LABELS.marketing,
    description:
      "We build marketing sites that behave like systems: fast, accessible, easy to own, and ready to connect to CRM, content, and analytics—without turning every change into a rebuild.",
    color: "teal",
    icon: shapeAsset("cube", "purple"),
  },
  {
    id: "ai-consulting",
    title: EXPERTISE_LABELS["ai-consulting"],
    description:
      "We help you use AI where it genuinely helps—augmenting judgment and speeding tedious work—without baking in dependency or risk you cannot own. Practical integrations, not slide-deck promises.",
    color: "blue",
    icon: shapeAsset("arch", "pink"),
  },
] as const;

export const SERVICE_ITEMS: Readonly<
  Record<ExpertiseCategoryKey, readonly string[]>
> = {
  strategy: [
    "Software Consulting",
    "Prototype Design",
    "Technology Analysis",
    "Software Architecture",
    "Technical Planning",
    "Product Strategy",
    "System Integration",
    "Platform Selection",
    "Technical Roadmapping",
    "Feasibility Studies",
    "Security Planning",
    "Performance Strategy",
  ],
  design: [
    "Wireframing",
    "Service Design",
    "User Experience",
    "Interface Design",
    "User Research",
    "Interaction Design",
    "Design Systems",
    "Prototyping",
    "Usability Testing",
    "Information Architecture",
    "Design Sprints",
    "User Journey Mapping",
  ],
  development: [
    "Web Applications",
    "Mobile Applications",
    "API Development",
    "Technical Architecture",
    "Frontend Development",
    "Backend Development",
    "Database Design",
    "Cloud Infrastructure",
    "DevOps Integration",
    "Performance Optimization",
    "Security Implementation",
    "Code Reviews",
  ],
  prototyping: [
    "Proof-of-concept builds",
    "MVP scoping and delivery",
    "Validation experiments",
    "Technical spikes",
    "Risk-first sequencing",
    "Demo-ready prototypes",
    "Iteration with real users",
  ],
  brand: [
    "Logo Design",
    "Brand Guidelines",
    "Marketing Materials",
    "Sales Decks and Presentations",
    "Visual Identity",
    "Brand Strategy",
    "Motion Graphics",
    "Style Guides",
    "Brand Voice",
    "Digital Assets",
    "Print Materials",
    "Brand Workshops",
  ],
  marketing: [
    "Marketing site architecture",
    "CMS and content modeling",
    "Performance and Core Web Vitals",
    "Design systems for marketing",
    "Analytics and measurement",
    "Integrations (CRM, email, data)",
    "Accessibility and SEO foundations",
    "Component libraries for editors",
  ],
  "ai-consulting": [
    "Use-case discovery",
    "Tooling and vendor fit",
    "Human-in-the-loop workflows",
    "Data and privacy guardrails",
    "Prompt and workflow design",
    "Internal tooling integrations",
    "Evaluation and monitoring",
    "Training and handoff",
  ],
};
